import React from 'react';

/**
 * ErrorBoundary — catches render-time throws so a bug in one branch of the
 * tree never blanks the whole SPA. Ships a friendly inline message with a
 * "Reload" affordance instead of the browser's default white screen.
 *
 * Usage:
 *   <ErrorBoundary fallbackTitle="Digital Health File — temporarily unavailable">
 *     <HealthFileSection ... />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, err: null };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, err };
  }

  componentDidCatch(err, info) {
    console.error('[ErrorBoundary]', err, info?.componentStack);
    // Best-effort telemetry to the backend — never blocks the fallback.
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/client-errors';
      const payload = {
        message: String(err?.message || err),
        component: this.props.fallbackTitle || 'unknown',
        stack: (info?.componentStack || '').slice(0, 2000),
        href: typeof window !== 'undefined' ? window.location.href : '',
      };
      // fire-and-forget POST — swallow every failure.
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  }

  render() {
    if (this.state.hasError) {
      const Fallback = this.props.fallback;
      if (Fallback) return <Fallback err={this.state.err} onRetry={() => this.setState({ hasError: false, err: null })} />;
      return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="error-boundary-fallback">
          <p className="text-sm font-semibold text-amber-900">
            {this.props.fallbackTitle || 'Something went wrong here.'}
          </p>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">
            The rest of your dashboard is safe and still working. Please refresh this section — if it keeps happening, our team has been alerted.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => this.setState({ hasError: false, err: null })}
              className="text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => (typeof window !== 'undefined' ? window.location.reload() : null)}
              className="text-xs font-semibold bg-white hover:bg-amber-100 text-amber-800 border border-amber-300 px-3 py-1.5 rounded-lg"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
